import {ProgressAria} from '_components/ProgressAria';
import LFCSingleValue from '_components/surfaces/LFCSingleValue';
import {fmtThreeCommas} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import {green, red} from '@mui/material/colors';

import {calculatePercent} from '../logics';
import {GraphBaseData, GraphSelectedData, MachingTrendData, SearchConditions} from '../types';
import {CloudDistanceDig} from './cloud-distance-dig';
import {CloudDistanceDialogParams} from './cloud-distance-dig/types';
import {CloudDistanceMatchBoxChart} from './CloudDistanceMatchBoxChart';

interface MapDetailProps {
  searchConditions: SearchConditions;
  selectedMap: string;
  xTimeliles: string[];
}

export const MapDetail = ({searchConditions, selectedMap, xTimeliles}: MapDetailProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const {parseNumber, parsePercent} = useParseWithSettings();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  const [dsInspectionSummaryData, setDsInspectionSummaryData] = useState<Record<
    string,
    any
  > | null>(null);
  const [zoomChartOpen, setZoomChartOpen] = useState<string>('');
  const [dsMachingTrendData, setDsMachingTrendDatas] = useState<MachingTrendData[]>([]);
  const [graphBaseData, setGraphBaseData] = useState<GraphBaseData>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogParameters, setDialogParameters] = useState<CloudDistanceDialogParams>();

  /**
   * 初期処理
   */
  useEffect(() => {
    if (searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      //マッチング率
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30003,
        parameters: {
          search_type: searchConditions.searchType,
          select_date_from: searchConditions.selectDateFrom,
          select_date_to: searchConditions.selectDateTo,
          work: !isEmpty(searchConditions.work)
            ? '{' + searchConditions.work.join(',') + '}'
            : null,
          deviceid: !isEmpty(searchConditions.deviceid)
            ? '{' + searchConditions.deviceid.join(',') + '}'
            : null,
          judge: searchConditions.judge === 9 ? null : searchConditions.judge,
          map: selectedMap
        },
        name: 'マッチング率',
        cancelToken: cancelSource.current.token,
        t
      }).then(data => {
        setDsMachingTrendDatas(data);
      }),
      //検査数等集計データ
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30008,
        parameters: {
          // search_type: searchConditions.searchType,
          select_date_from: searchConditions.selectDateFrom,
          select_date_to: searchConditions.selectDateTo,
          work: !isEmpty(searchConditions.work)
            ? '{' + searchConditions.work.join(',') + '}'
            : null,
          deviceid: !isEmpty(searchConditions.deviceid)
            ? '{' + searchConditions.deviceid.join(',') + '}'
            : null,
          judge: searchConditions.judge === 9 ? null : searchConditions.judge,
          map: selectedMap
        },
        name: '検査数等集計データ',
        cancelToken: cancelSource.current.token,
        t
      }).then(data => {
        setDsInspectionSummaryData(data[0]);
      })
    ]).finally(() => {
      setStartProcess(false);
    });

    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  /**
   * チャートデータ生成処理
   */
  useEffect(() => {
    if (dsMachingTrendData.length === 0) return;

    const tmp_row: GraphBaseData = {
      data_upper_distance: [],
      data_lower_distance: [],
      data_upper_limits: [],
      data_lower_limits: [],
      upper_distance_min: 0,
      upper_distance_max: 0,
      lower_distance_min: 0,
      lower_distance_max: 0,
      limits_min: 0,
      limits_max: 0,
      hasMultipleLimit: false,
      xlist: [],
      detail: [],
      map: selectedMap
    };

    if (searchConditions.searchType === 0) {
      //検査毎
      dsMachingTrendData.forEach((r: MachingTrendData) => {
        tmp_row.xlist.push(String(r.idx));
        tmp_row.detail.push(r);
        //検査毎の場合だけ数値が直に入ってる
        tmp_row.data_upper_distance.push(Number(r.upper_distance));
        tmp_row.data_lower_distance.push(Number(r.lower_distance));
        tmp_row.data_upper_limits.push(Number(r.upper_limit));
        tmp_row.data_lower_limits.push(Number(r.lower_limit));
      });
      tmp_row.upper_distance_min = parseNumber(
        Math.min(...(tmp_row.data_upper_distance as Array<number>))
      );
      tmp_row.upper_distance_max = parseNumber(
        Math.max(...(tmp_row.data_upper_distance as Array<number>))
      );
      tmp_row.lower_distance_min = parseNumber(
        Math.min(...(tmp_row.data_lower_distance as Array<number>))
      );
      tmp_row.lower_distance_max = parseNumber(
        Math.max(...(tmp_row.data_lower_distance as Array<number>))
      );
      tmp_row.limits_min = parseNumber(Math.min(...(tmp_row.data_lower_limits as Array<number>)));
      tmp_row.limits_max = parseNumber(Math.max(...(tmp_row.data_upper_limits as Array<number>)));
    } else {
      xTimeliles.forEach((lq_time_string: string) => {
        let tmp_upper_distance: number[] = [];
        let tmp_lower_distance: number[] = [];
        let tmp_upper_limits: number[] = [];
        let tmp_lower_limits: number[] = [];
        let tval = 0;
        dsMachingTrendData.forEach((r: MachingTrendData) => {
          if (
            (searchConditions.searchType === 1 && r.year_month_day_hour === lq_time_string) ||
            (searchConditions.searchType === 2 && r.year_month_day === lq_time_string) ||
            (searchConditions.searchType === 3 && r.year_month === lq_time_string)
          ) {
            tmp_upper_distance.push(Number(r.upper_distance));
            tmp_lower_distance.push(Number(r.lower_distance));
            tmp_upper_limits.push(Number(r.upper_limit));
            tmp_lower_limits.push(Number(r.lower_limit));
          }
        });
        tmp_row.xlist.push(lq_time_string);
        // tmp_row.detail = ; 検査毎以外はdetailをセットしない
        tmp_row.data_upper_distance.push(tmp_upper_distance);
        tmp_row.data_lower_distance.push(tmp_lower_distance);
        tmp_row.data_upper_limits.push(tmp_upper_limits);
        tmp_row.data_lower_limits.push(tmp_lower_limits);

        tval = parseNumber(Math.min(...tmp_upper_distance));
        if (tval < tmp_row.upper_distance_min) {
          tmp_row.upper_distance_min = tval;
        }
        tval = parseNumber(Math.max(...tmp_upper_distance));
        if (tmp_row.upper_distance_max < tval) {
          tmp_row.upper_distance_max = tval;
        }
        tval = parseNumber(Math.min(...tmp_lower_distance));
        if (tval < tmp_row.lower_distance_min) {
          tmp_row.lower_distance_min = tval;
        }
        tval = parseNumber(Math.max(...tmp_lower_distance));
        if (tmp_row.lower_distance_max < tval) {
          tmp_row.lower_distance_max = tval;
        }
        tval = parseNumber(Math.min(...tmp_lower_limits));
        if (tval < tmp_row.limits_min) {
          tmp_row.limits_min = tval;
        }
        tval = parseNumber(Math.max(...tmp_upper_limits));
        if (tmp_row.limits_max < tval) {
          tmp_row.limits_max = tval;
        }
      });
    }
    setGraphBaseData(tmp_row);
  }, [dsMachingTrendData]);

  /**
   * グラフクリック処理
   */
  const handleGraphClick = useCallback(
    (data: GraphSelectedData) => {
      setDialogParameters({
        pageName: `${t('menu.page_name.Cloud Distance')}: ${t('詳細')}`,
        selectDateFrom: searchConditions.selectDateFrom,
        selectDateTo: searchConditions.selectDateTo,
        work: searchConditions.work,
        deviceid: searchConditions.deviceid,
        judge: searchConditions.judge,
        map: data.mapName,
        searchType: searchConditions.searchType
      });
      setDialogOpen(true);
    },
    [searchConditions]
  );

  /**
   * ダイアログクローズ処理
   */
  const handleDialogClose = () => {
    // setOpenData({mapName: ''});
    setDialogOpen(false);
  };

  return (
    <ProgressAria startProcess={startProcess}>
      <Box mb={1}>
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary
            id={'MapDetail_' + selectedMap}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="MapDetail_content"
          >
            <Typography component="span" sx={{fontWeight: 'bold'}}>
              {selectedMap}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{padding: 1}}>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={8} md={9} lg={10}>
                {graphBaseData && (
                  <CloudDistanceMatchBoxChart
                    title={``}
                    source={graphBaseData}
                    searchType={searchConditions.searchType}
                    exportFilename={`Cloud Distance_` + selectedMap}
                    height={'22vh'}
                    onPointClick={handleGraphClick}
                    onClickDetailAnalysis={handleGraphClick}
                    zoomChartId={'zoom-CloudDistanceMatchBoxChart-' + selectedMap}
                    zoomedChartId={zoomChartOpen}
                    zoomCharts={() =>
                      setZoomChartOpen('zoom-CloudDistanceMatchBoxChart-' + selectedMap)
                    }
                    zoomChartsClose={() => setZoomChartOpen('')}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={2} sx={{display: 'flex', alignItems: 'center'}}>
                <Stack spacing={2} sx={{width: '100%'}}>
                  <LFCSingleValue
                    title={t('検査数')}
                    val={fmtThreeCommas(dsInspectionSummaryData?.total_count)}
                    titleVariant="caption"
                    valVariant="body1"
                    key={t('検査数')}
                    fontColor={green[500]}
                  />
                  <Box style={{marginTop: '2px'}}>
                    <Grid container>
                      <Grid item xs={6}>
                        <LFCSingleValue
                          title={t('良品')}
                          val={fmtThreeCommas(dsInspectionSummaryData?.ok_count)}
                          titleVariant="caption"
                          valVariant="body1"
                          key={t('良品')}
                          fontColor={green[500]}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <LFCSingleValue
                          title={t('不良品')}
                          val={fmtThreeCommas(dsInspectionSummaryData?.ng_count)}
                          titleVariant="caption"
                          valVariant="body1"
                          key={t('不良品')}
                          fontColor={red[500]}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <LFCSingleValue
                    title={t('不良率')}
                    val={parsePercent(
                      calculatePercent(
                        dsInspectionSummaryData?.ng_count || 0,
                        dsInspectionSummaryData?.total_count || 0
                      )
                    )}
                    titleVariant="caption"
                    valVariant="body1"
                    key={t('不良率')}
                    fontColor={red[500]}
                    unit={'%'}
                  />
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
      {dialogParameters && (
        <CloudDistanceDig open={dialogOpen} onClose={handleDialogClose} params={dialogParameters} />
      )}
    </ProgressAria>
  );
};
